import { type ReactNode, useMemo } from "react";
import SingleTechFilter from "~/Search/components/Filters/components/TechFilter/SingleTechFilter";
import { useDispatch, useSelector } from "react-redux";
import { type RootState } from "../../../../redux/store";
import { updateTechValueAction } from "../../../../Search/redux/actions";
import { Card } from "../../../../Configurators/Linear/components/Card";
import LinearInfo from "../../../../Configurators/Linear/components/LinearInfo";
import { useTranslation } from "react-i18next";
import type { TechFilter } from "~/Search/filterConfigModels";

interface Props {
  image?: ReactNode;
  items: TechFilter[];
  title: string;
}

interface CallbacksMap {
  [title: string]: (type: "min" | "max", value: number | undefined) => void;
}

export const DesktopTechFilterCategory = (props: Props) => {
  const values = useSelector(selectRawValues);
  const { items, image } = props;

  const dispatch = useDispatch();
  const callbacks = useMemo(() => {
    const callbacks: CallbacksMap = {};
    items.forEach((item) => {
      callbacks[item.uid] = (type, val) => dispatch(updateTechValueAction(item.uid, type, val));
    });

    return callbacks;
  }, [items, dispatch]);

  const renderChildren = renderItems(callbacks, values);

  return (
    <div className="scha__sa__tech-filter-category--desktop">
      <h2 className="scha__sa__tech-filter-category__title">{props.title}</h2>
      <div className="scha__sa__tech-filter-category__card-row">
        <Card disableHover={true} className="scha__sa__tech-filter-category__card">
          {renderChildren(items)}
        </Card>

        <div className="scha__sa__tech-filter-category__image">{image}</div>
      </div>
    </div>
  );
};

export const renderItems = (callbacks: CallbacksMap, values: Dict<number>) => (items: TechFilter[]) => {
  const { t } = useTranslation();
  const renderTitle = (item: TechFilter) => {
    const assistantText = item.assistantLabel ? t(item.assistantLabel) : "";

    return (
      <>
        {item.assistantLabel && <span dangerouslySetInnerHTML={{ __html: assistantText }} />}
        {item.description && <LinearInfo info={item.description} />}
      </>
    );
  };

  return (
    <>
      {items.map((item) => (
        <SingleTechFilter
          key={item.uid}
          title={<>{renderTitle(item)}</>}
          placeholderSuffix={item.unit} //design logic
          min={values[`${item.uid}.min`]}
          max={values[`${item.uid}.max`]}
          onUpdateValues={callbacks[item.uid]}
          data-test-id={`${item.uid}`}
        />
      ))}
    </>
  );
};

const selectRawValues = (state: RootState) => state.srp.rawTechFilterValues;
